import React from 'react';
import { scaleExpr } from './HeroGrid';

type Variant = 'a' | 'b' | 'c' | 'd';

interface CubeClusterProps {
  variant?: Variant;
}

/** Internal 4×4 slot grid, symmetric padding (designed for BASE_CELL=120 with
 *  14px cubes; all values scale to the live cell via scaleExpr). */
const PAD = 8;
const CUBE = 14;
const STEP = 30;

type CubePos = { x: number; y: number; delay: number };

/** ~7 visible slots per variant — chosen for visual balance, not a uniform grid. */
const VARIANTS: Record<Variant, CubePos[]> = {
  a: [
    { x: 0, y: 0, delay: 0.0 },
    { x: 1, y: 0, delay: 0.9 },
    { x: 0, y: 1, delay: 1.8 },
    { x: 2, y: 1, delay: 2.7 },
    { x: 3, y: 1, delay: 3.6 },
    { x: 1, y: 2, delay: 4.5 },
    { x: 2, y: 3, delay: 5.4 },
  ],
  b: [
    { x: 1, y: 0, delay: 0.0 },
    { x: 3, y: 0, delay: 1.0 },
    { x: 0, y: 1, delay: 2.0 },
    { x: 2, y: 2, delay: 3.0 },
    { x: 3, y: 2, delay: 4.0 },
    { x: 1, y: 3, delay: 5.0 },
    { x: 0, y: 3, delay: 6.0 },
  ],
  c: [
    { x: 0, y: 0, delay: 0.3 },
    { x: 2, y: 0, delay: 1.4 },
    { x: 1, y: 1, delay: 2.5 },
    { x: 3, y: 2, delay: 3.6 },
    { x: 0, y: 2, delay: 4.0 },
    { x: 2, y: 3, delay: 5.1 },
    { x: 3, y: 3, delay: 6.2 },
  ],
  d: [
    { x: 1, y: 0, delay: 0.5 },
    { x: 0, y: 1, delay: 1.6 },
    { x: 2, y: 1, delay: 2.7 },
    { x: 3, y: 1, delay: 3.4 },
    { x: 1, y: 2, delay: 4.5 },
    { x: 3, y: 3, delay: 5.6 },
  ],
};

/** Renders a flickering cube cluster filling a single grid cell, anchored at the
 *  cell origin (0,0). HeroDecorations positions the cell. */
const CubeCluster: React.FC<CubeClusterProps> = ({ variant = 'a' }) => {
  const cubes = VARIANTS[variant];

  return (
    <div
      aria-hidden
      className="absolute top-0 left-0 animate-cluster-envelope"
      style={{ width: 'var(--grid-cell, 100px)', height: 'var(--grid-cell, 100px)' }}
    >
      {/* Corner diamond markers anchored to the cell's corners. */}
      <span className="absolute top-0 left-0    -translate-x-1/2 -translate-y-1/2 rotate-45 bg-slate-300/80" style={{ width: scaleExpr(6), height: scaleExpr(6) }} />
      <span className="absolute top-0 right-0    translate-x-1/2 -translate-y-1/2 rotate-45 bg-slate-300/80" style={{ width: scaleExpr(6), height: scaleExpr(6) }} />
      <span className="absolute bottom-0 left-0  -translate-x-1/2  translate-y-1/2 rotate-45 bg-slate-300/80" style={{ width: scaleExpr(6), height: scaleExpr(6) }} />
      <span className="absolute bottom-0 right-0  translate-x-1/2  translate-y-1/2 rotate-45 bg-slate-300/80" style={{ width: scaleExpr(6), height: scaleExpr(6) }} />

      {/* Cubes — each flickers independently within the cluster's envelope window. */}
      {cubes.map((c, i) => (
        <span
          key={i}
          className="absolute bg-slate-300 animate-cube-flicker"
          style={{
            left: scaleExpr(PAD + c.x * STEP),
            top: scaleExpr(PAD + c.y * STEP),
            width: scaleExpr(CUBE),
            height: scaleExpr(CUBE),
            borderRadius: scaleExpr(2.5),
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

export default CubeCluster;
